import { CartAddEvent, ThemeEvents } from '@theme/events';
const SINGLE_IDS = new Set([8386887778483, 8280638816435, 8280637407411]);
const PACK_IDS = new Set([8658582798515, 8658582864051, 8658582896819]);
const ROUNDING_BUFFER = 5;
let addPending = false;
let uncertainWrite = false;
let disabledControls = new Map();
let lockObserver;
let recoveryPending = false;
let recoveryMessage = '';
let recoveryPanel;
const MUTATION_CONTROLS = [
  'beest-cart-offer button:not([data-beest-refresh])', 'beest-cart-offer input', 'beest-cart-offer select',
  'button.quantity-minus', 'button.quantity-plus', 'input[name="updates[]"][data-cart-line]', 'input[name="quantity"]',
  'button.cart-items__remove', '[data-cart-remove]',
  '.cart-discount__input', '.cart-discount__button', '.cart-discount__pill-remove',
  'product-form-component button[type="submit"]', 'form[action*="/cart/add"] button[type="submit"]',
  'form[action*="/cart/add"] input[type="submit"]', 'button[name="add"]', 'input[name="add"]',
  'button[name="checkout"]', 'input[name="checkout"]'
].join(',');

export function offerMode(cart, all = false) {
  if (!cart || cart.currency !== 'USD' || !(Number(cart.total_price) > 0) || !(cart.items || []).length) return '';
  let singles = 0, packs = 0, applied = false;
  for (const item of cart.items) {
    const id = Number(item.product_id), quantity = Number(item.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) return '';
    if (SINGLE_IDS.has(id)) singles += quantity;
    if (PACK_IDS.has(id)) packs += quantity;
    for (const allocation of item.line_level_discount_allocations || []) {
      if (String(allocation.discount_application?.title || '').includes('Make it there')) applied = true;
    }
  }
  if (all) return applied ? '' : 'extra';
  if (singles === 2 || (packs >= 1 && singles === 0)) return 'third';
  if (singles === 1 && packs === 0) return 'second';
  return '';
}
export function hasEligibleBasket(cart) { return offerMode(cart) !== ''; }
export function collectSectionIds(nodes) {
  return [...new Set(Array.from(nodes, node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
}
function route(path) { return (globalThis.Shopify?.routes?.root || '/') + path; }
async function readCart() {
  const response = await fetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Your cart could not be refreshed. Please try again.');
  return response.json();
}
function announce(message) {
  let live = document.getElementById('beest-cart-offer-announcement');
  if (!live) {
    live = document.createElement('div'); live.id = 'beest-cart-offer-announcement';
    live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite');
    live.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    document.body.append(live);
  }
  live.textContent = message;
}
function instances() { return Array.from(document.querySelectorAll('beest-cart-offer')); }
function money(cents) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100); }
function disableMutationControls(root = document) {
  // Serialize cart writes without disabling navigation, drawer close, search or disclosures.
  const controls = Array.from(root.querySelectorAll?.(MUTATION_CONTROLS) || []);
  if (root.matches?.(MUTATION_CONTROLS)) controls.unshift(root);
  for (const control of controls) {
    if (!('disabled' in control)) continue;
    if (!disabledControls.has(control)) disabledControls.set(control, control.disabled);
    control.disabled = true;
  }
  for (const node of instances()) node.setAttribute('aria-busy', 'true');
}
function syncRecoveryControls() {
  if (!uncertainWrite) return;
  const buttons = instances().flatMap(node => Array.from(node.querySelectorAll('[data-beest-refresh]')));
  for (const button of buttons) { button.hidden = false; button.disabled = recoveryPending; }
  if (buttons.length) { recoveryPanel?.remove(); recoveryPanel = undefined; return; }
  // An empty-cart morph can remove every offer element. Keep a working recovery outside it.
  if (!recoveryPanel) {
    recoveryPanel = document.createElement('div');
    recoveryPanel.id = 'beest-cart-offer-recovery';
    recoveryPanel.setAttribute('role', 'status');
    recoveryPanel.style.cssText = 'padding:16px;margin:12px;background:#F4ECCD;color:#7A1E1E;border:1px solid #7A1E1E';
    const message = document.createElement('p');
    const button = document.createElement('button');
    button.type = 'button'; button.textContent = 'Refresh cart'; button.setAttribute('data-beest-refresh', '');
    button.style.cssText = 'min-height:44px;padding:10px;cursor:pointer';
    button.addEventListener('click', () => { void recoverPendingCart(); });
    recoveryPanel.append(message, button);
  }
  const message = recoveryPanel.querySelector('p');
  if (message.textContent !== recoveryMessage) message.textContent = recoveryMessage;
  recoveryPanel.querySelector('button').disabled = recoveryPending;
  const host = document.querySelector('cart-drawer-component dialog[open] .cart-drawer__content') || document.querySelector('main') || document.body;
  if (!host.contains(recoveryPanel)) host.append(recoveryPanel);
}
async function recoverPendingCart() {
  if (!uncertainWrite || recoveryPending) return;
  recoveryPending = true; syncRecoveryControls();
  let current;
  try { current = await readCart(); }
  catch {
    recoveryPending = false;
    recoveryMessage = 'Your update is still unconfirmed. Refresh the cart before making another change.';
    for (const node of instances()) node.feedback(recoveryMessage);
    announce(recoveryMessage); syncRecoveryControls(); return;
  }
  // The read confirms current state even if the original element was disconnected.
  endMutation(); confirmedCart(current);
  for (const node of instances()) node.feedback('Cart refreshed. Review your current total before another change.');
}
function beginMutation() {
  if (addPending) return false;
  addPending = true; uncertainWrite = false; disableMutationControls();
  lockObserver = new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) disableMutationControls(node);
    syncRecoveryControls();
  });
  lockObserver.observe(document.body, { childList: true, subtree: true });
  return true;
}
function endMutation() {
  lockObserver?.disconnect(); lockObserver = undefined;
  addPending = false; uncertainWrite = false;
  recoveryPending = false; recoveryMessage = ''; recoveryPanel?.remove(); recoveryPanel = undefined;
  for (const [control, wasDisabled] of disabledControls) if (control.isConnected) control.disabled = wasDisabled;
  disabledControls = new Map();
  for (const node of instances()) {
    node.removeAttribute('aria-busy'); node.updateButtons?.();
    for (const button of node.querySelectorAll('[data-beest-refresh]')) button.hidden = true;
  }
}
// Also prevent native cart links and keyboard form submissions during an in-flight/uncertain write.
document.addEventListener('click', event => {
  if (addPending && event.target instanceof Element && event.target.closest('a[href*="/cart/change"],a[href*="/cart/clear"],[data-cart-remove]')) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);
document.addEventListener('submit', event => {
  if (addPending && event.target instanceof HTMLFormElement && /\/cart(?:\/(?:add|change|update|clear))?(?:\.js)?(?:[/?#]|$)/.test(event.target.action)) {
    event.preventDefault(); event.stopImmediatePropagation();
  }
}, true);

// A shipping claim requires inspectable current percentage discounts. Unknown fixed/scoped discounts are never guessed.
export function percentageFactor(cart) {
  const applications = [], seen = new Set(), codeTitles = new Set();
  let allocated = 0;
  const add = (app, amount) => {
    if (!app) return false;
    if (app.target_type === 'shipping_line') return true;
    const title = String(app.title || '');
    const knownOffer = title.includes('Make it there') && app.value_type === 'fixed_amount';
    const percentage = app.value_type === 'percentage' && app.target_selection === 'all' && app.target_type === 'line_item';
    if (!knownOffer && !percentage) return false;
    if (app.type === 'discount_code') codeTitles.add(title.toUpperCase());
    if (percentage) {
      if (app.value == null || String(app.value).trim() === '') return false;
      const value = Number(app.value);
      if (!Number.isFinite(value) || value < 0 || value > 100) return false;
      const key = app.key || [app.type, title, value, app.target_type, app.target_selection].join('|');
      if (!seen.has(key)) { seen.add(key); applications.push(value); }
    }
    allocated += Math.max(0, Number(amount) || 0);
    return true;
  };
  const orderApplications = cart.cart_level_discount_applications || [];
  for (const entry of orderApplications.length ? orderApplications : cart.discount_applications || []) {
    const app = entry.discount_application || entry;
    if (!add(app, entry.amount ?? app.total_allocated_amount)) return null;
  }
  for (const item of cart.items || []) {
    for (const allocation of item.line_level_discount_allocations || []) if (!add(allocation.discount_application, allocation.amount)) return null;
  }
  if (Number(cart.total_discount) > 0 && allocated === 0) return null;
  for (const code of cart.discount_codes || []) {
    if (code.applicable !== false && !codeTitles.has(String(code.code || '').toUpperCase())) return null;
  }
  // Summing percentages is conservative even if the store applies multiple percentages sequentially.
  return Math.max(0, 1 - applications.reduce((sum, value) => sum + value, 0) / 100);
}
function originalTotal(cart) {
  const total = Number(cart.original_total_price);
  if (cart.original_total_price != null && Number.isSafeInteger(total) && total >= 0) return total;
  let value = 0;
  for (const item of cart.items || []) {
    const amount = Number(item.original_line_price);
    if (!Number.isSafeInteger(amount) || amount < 0) return null;
    value += amount;
  }
  return Number.isSafeInteger(value) ? value : null;
}
function singleCount(cart) {
  if (!(cart.items || []).length) return 0;
  let count = 0;
  for (const item of cart.items) {
    if (!SINGLE_IDS.has(Number(item.product_id)) || item.selling_plan_allocation || Number(item.original_price) !== 1299 || !Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 1) return 0;
    count += Number(item.quantity);
  }
  return count;
}
function qualifies(baseCents, factor, threshold) {
  return Number.isSafeInteger(baseCents) && factor !== null && Math.floor(baseCents * factor) >= threshold + ROUNDING_BUFFER;
}
export function shippingPlan(cart, config) {
  if (!cart || cart.currency !== 'USD' || config.country !== 'US' || Number(cart.total_price) >= config.threshold) return null;
  const count = singleCount(cart), factor = percentageFactor(cart);
  if ((count !== 1 && count !== 2) || factor === null) return null;
  const total = originalTotal(cart);
  if (total === null || !config.choices.length || config.choices.some(choice => choice.price !== 1299)) return null;
  const addedCount = 3 - count;
  const additionQualifies = qualifies(total + addedCount * 1299 - 300, factor, config.threshold);
  const onlyLine = count === 1 && cart.items.length === 1 && Number(cart.items[0].quantity) === 1 ? cart.items[0] : null;
  const herdQualifies = Boolean(onlyLine && config.herdAvailable && Number.isSafeInteger(config.herdId) && config.herdPrice > 0 && qualifies(config.herdPrice, factor, config.threshold));
  if (!additionQualifies && !herdQualifies) return null;
  return { count, addedCount, lineKey: onlyLine?.key || '', herdQualifies, additionQualifies };
}
function confirmedCart(cart, sections = {}, variantId) {
  for (const node of instances()) node.renderCart?.(cart);
  document.dispatchEvent(new CartAddEvent(cart, 'beest-cart-offer', { source: 'beest-cart-offer', variantId, itemCount: cart.item_count, sections }));
}
function quantityOf(cart, variantId) {
  return (cart.items || []).reduce((sum, item) => sum + (Number(item.variant_id || item.id) === variantId ? Number(item.quantity) : 0), 0);
}
function mutationConfirmed(before, after, action) {
  if (action.kind === 'switch') return !after.items.some(item => item.key === action.lineKey) && quantityOf(after, action.variantId) >= quantityOf(before, action.variantId) + 1;
  return action.items.every(item => quantityOf(after, item.id) >= quantityOf(before, item.id) + item.quantity);
}

class BeestCartOffer extends HTMLElement {
  connectedCallback() {
    this._listeners?.abort(); this._listeners = new AbortController();
    const signal = this._listeners.signal;
    this.addEventListener('change', event => { if (event.target.matches('[data-beest-choice]')) this.updateButtons(); }, { signal });
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('[data-beest-refresh]')) { event.preventDefault(); void this.recoverCart(); }
      else if (button.matches('[data-beest-add]')) { event.preventDefault(); void this.mutate('offer'); }
      else if (button.matches('[data-beest-switch]')) { event.preventDefault(); void this.mutate('switch'); }
      else if (button.matches('[data-beest-recommend]')) { event.preventDefault(); void this.mutate('recommend'); }
    }, { signal });
    // Native discount events fire just before synchronous morphSection. Refresh after that call stack,
    // including when the custom element is preserved and connectedCallback does not run again.
    document.addEventListener(ThemeEvents.cartUpdate, () => this.scheduleCartRefresh(), { signal });
    document.addEventListener(ThemeEvents.discountUpdate, () => this.scheduleCartRefresh(), { signal });
    this._stateObserver?.disconnect();
    this._stateObserver = new MutationObserver(records => {
      const isSnapshot = node => node?.nodeType === 1 && (node.matches?.('[data-beest-cart-state]') || node.querySelector?.('[data-beest-cart-state]'));
      const snapshotChanged = records.some(record => record.type !== 'attributes' &&
        (isSnapshot(record.target) || isSnapshot(record.target?.parentNode) ||
        (record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(isSnapshot))));
      const visibilityReset = records.some(record => {
        if (record.type !== 'attributes' || record.attributeName !== 'hidden') return false;
        const node = record.target;
        let expectedHidden;
        if (node.matches?.('[data-beest-shipping-recommendation]')) expectedHidden = !this._plan;
        else if (node.matches?.('[data-beest-herd-path]')) expectedHidden = !this._plan?.herdQualifies;
        else if (node.matches?.('[data-beest-add-path]')) expectedHidden = !this._plan?.additionQualifies;
        else return false;
        return node.hidden !== expectedHidden;
      });
      if (snapshotChanged || visibilityReset) this.scheduleCartRefresh();
    });
    // Native drawer morphs can reset hidden without changing the snapshot. Reconcile only mismatches
    // against the current plan; our own correct hidden writes never schedule another render.
    this._stateObserver.observe(this, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.updateButtons();
    this.scheduleCartRefresh();
    if (addPending) disableMutationControls();
  }
  disconnectedCallback() {
    this._listeners?.abort();
    this._stateObserver?.disconnect();
    this._refreshTicket = (this._refreshTicket || 0) + 1;
  }
  scheduleCartRefresh() {
    const ticket = this._refreshTicket = (this._refreshTicket || 0) + 1;
    queueMicrotask(() => {
      if (!this.isConnected || ticket !== this._refreshTicket) return;
      const rendered = this.readRenderedCart();
      if (rendered) {
        if (this.isConnected && ticket === this._refreshTicket) this.renderCart(rendered);
        return;
      }
      // Legacy sections without a snapshot can still use a read-only refresh.
      void readCart().then(cart => {
        if (this.isConnected && ticket === this._refreshTicket) this.renderCart(cart);
      }).catch(error => {
        if (this.isConnected && ticket === this._refreshTicket) {
          this.feedback(error instanceof Error ? error.message : 'Your cart could not be refreshed.');
        }
      });
    });
  }
  readRenderedCart() {
    const state = this.querySelector('[data-beest-cart-state]');
    if (!state) return null;
    try {
      const cart = JSON.parse(state.textContent || '');
      if (cart?.currency !== 'USD' || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) return null;
      return cart;
    } catch (error) {
      return null;
    }
  }
  config() {
    // These choices remain present after Shopify removes the already-used optional offer.
    const available = this.querySelectorAll('[data-beest-recommendation-flavor] option');
    const choices = [...new Map(Array.from(available, node => [Number(node.value), { id: Number(node.value), price: Number(node.dataset.price), name: node.textContent }])).values()];
    return { country: this.dataset.country, threshold: Number(this.dataset.threshold), choices, herdId: Number(this.dataset.herdId), herdPrice: Number(this.dataset.herdPrice), herdAvailable: this.dataset.herdAvailable === 'true' };
  }
  renderCart(cart) {
    this._cart = cart; const config = this.config(); this._plan = shippingPlan(cart, config);
    const remaining = Math.max(0, config.threshold - Number(cart.total_price));
    const status = this.querySelector('.beest-cart-status'), fill = this.querySelector('.beest-cart-fill'), track = this.querySelector('.beest-cart-track');
    if (status) { status.textContent = remaining ? "You're " + money(remaining) + ' from free shipping' : 'Free shipping unlocked'; status.classList.toggle('beest-cart-status--free', remaining === 0); }
    if (fill) { fill.style.width = Math.max(0, Math.min(100, Number(cart.total_price) / config.threshold * 100)) + '%'; fill.classList.toggle('beest-cart-fill--free', remaining === 0); }
    track?.setAttribute('aria-valuenow', String(Math.min(Number(cart.total_price), config.threshold) / 100));
    const panel = this.querySelector('[data-beest-shipping-recommendation]');
    if (panel) panel.hidden = !this._plan;
    const herd = this.querySelector('[data-beest-herd-path]'), addition = this.querySelector('[data-beest-add-path]');
    if (herd) herd.hidden = !this._plan?.herdQualifies;
    if (addition) addition.hidden = !this._plan?.additionQualifies;
    const secondChoice = this.querySelector('[data-beest-recommendation-second]');
    if (secondChoice) { secondChoice.hidden = this._plan?.addedCount !== 2; secondChoice.disabled = this._plan?.addedCount !== 2; }
    const copy = this.querySelector('[data-beest-recommendation-copy]');
    if (copy && this._plan) copy.textContent = this._plan.addedCount === 2 ? 'Keep your bag and add two more to get free US shipping.' : 'Add one more bag to get free US shipping.';
    const button = this.querySelector('[data-beest-recommend]');
    if (button && this._plan) button.textContent = this._plan.addedCount === 2 ? 'Add two bags for free shipping' : 'Add one bag for free shipping';
    const mode = offerMode(cart, this.dataset.all === 'true');
    const optional = this.querySelector('[data-beest-optional-panel]');
    if (optional) optional.hidden = !mode;
    this.dataset.mode = mode;
    if (mode) {
      const offerPrice = mode === 'second' ? 1299 : 999;
      for (const choice of this.querySelectorAll('[data-beest-choice]')) { choice.dataset.offerPrice = String(offerPrice); choice.dataset.priceLabel = money(offerPrice); }
      const copy = this.querySelector('.beest-cart-offer-copy');
      if (copy) copy.textContent = mode === 'second' ? "Add a second bag and we'll take $3.00 off a third." : 'Add a single bag for $9.99. Save $3.00.';
      const legend = this.querySelector('.beest-cart-flavors legend');
      if (legend) legend.textContent = mode === 'second' ? 'Pick your second bag' : 'Pick your extra bag';
      const fineprint = this.querySelector('.beest-cart-fineprint');
      if (fineprint) fineprint.hidden = mode === 'second';
    }
    const base = originalTotal(cart), factor = percentageFactor(cart);
    const extraBase = base === null ? null : base + 1299 - (mode === 'second' ? 0 : 300);
    const clears = remaining > 0 && qualifies(extraBase, factor, config.threshold);
    const heading = this.querySelector('[data-beest-offer-heading]');
    if (heading) heading.textContent = clears ? 'Make it there.' : 'One more crunch';
    const benefit = this.querySelector('[data-beest-benefit]');
    if (benefit) { benefit.hidden = !clears; benefit.textContent = clears ? 'Unlock free shipping with your extra bag.' : ''; }
    this.updateButtons();
  }
  updateButtons() {
    const choice = this.querySelector('[data-beest-choice]:checked'), button = this.querySelector('[data-beest-add]');
    if (button) {
      button.disabled = addPending || !choice || !this.dataset.mode;
      if (!addPending && choice) button.textContent = 'Add ' + choice.dataset.name + ' · ' + choice.dataset.priceLabel;
    }
    for (const node of this.querySelectorAll('[data-beest-switch],[data-beest-recommend]')) node.disabled = addPending || !this._plan;
  }
  feedback(message) {
    for (const node of this.querySelectorAll('[data-beest-feedback]')) { node.textContent = message; node.hidden = !message; }
    if (message) announce(message);
  }
  showRecovery(message) {
    uncertainWrite = true; recoveryMessage = message; this.feedback(message);
    syncRecoveryControls();
  }
  async recoverCart() {
    await recoverPendingCart();
  }
  async mutate(kind) {
    if (addPending || this.dataset.country !== 'US' || this.dataset.currency !== 'USD') return;
    const config = this.config(), displayedPlan = this._plan;
    let action;
    if (kind === 'offer') {
      if (!this.dataset.mode) return;
      const choice = this.querySelector('[data-beest-choice]:checked'), id = Number(choice?.value);
      if (!choice || !Number.isSafeInteger(id) || Number(choice.dataset.offerPrice) !== (this.dataset.mode === 'second' ? 1299 : 999)) return;
      action = { kind, items: [{ id, quantity: 1 }], variantId: id };
    } else {
      if (!displayedPlan) return;
      if (kind === 'switch') {
        if (!displayedPlan.herdQualifies || !displayedPlan.lineKey) return;
        action = { kind, lineKey: displayedPlan.lineKey, variantId: config.herdId };
      } else {
        if (!displayedPlan.additionQualifies) return;
        const selections = Array.from(this.querySelectorAll('[data-beest-recommendation-flavor]')).slice(0, displayedPlan.addedCount);
        const quantities = new Map();
        for (const select of selections) { const id = Number(select.value); if (!config.choices.some(choice => choice.id === id)) return; quantities.set(id, (quantities.get(id) || 0) + 1); }
        if (selections.length !== displayedPlan.addedCount) return;
        action = { kind, items: Array.from(quantities, ([id, quantity]) => ({ id, quantity })), variantId: Number(selections[0].value) };
      }
    }
    if (!beginMutation()) return;
    this.feedback('Updating your cart…');
    let before, requestStarted = false;
    try {
      before = await readCart();
      if (kind === 'offer') {
        if (offerMode(before, this.dataset.all === 'true') !== this.dataset.mode) { confirmedCart(before); throw new Error('Your cart has changed. Review the refreshed offer before adding.'); }
      } else {
        const freshPlan = shippingPlan(before, config);
        const valid = freshPlan && freshPlan.count === displayedPlan.count && (kind === 'switch' ? freshPlan.herdQualifies && freshPlan.lineKey === action.lineKey && before.items.length === 1 && Number(before.items[0].quantity) === 1 : freshPlan.additionQualifies && freshPlan.addedCount === displayedPlan.addedCount);
        if (!valid) { confirmedCart(before); throw new Error('Your cart has changed. Review the refreshed recommendation before continuing.'); }
      }
      const sections = collectSectionIds(document.querySelectorAll('cart-items-component'));
      const body = { sections: sections.join(','), sections_url: location.pathname };
      let url;
      if (kind === 'switch') {
        // One atomic request replaces the freshly revalidated single line. Codes remain managed by Shopify.
        body.updates = { [action.lineKey]: 0, [String(action.variantId)]: 1 }; url = route('cart/update.js');
      } else { body.items = action.items; url = globalThis.Theme?.routes?.cart_add_url || route('cart/add.js'); }
      requestStarted = true;
      const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'That change could not be added. Review your cart and try again.');
      const current = await readCart(); confirmedCart(current, result.sections || {}, action.variantId);
      if (!mutationConfirmed(before, current, action)) this.feedback('Cart refreshed. Review the items and actual total before another change.');
      else announce(kind === 'switch' ? 'The Herd is in your cart. Your actual total and shipping progress have been updated.' : 'Bags added. Your actual total and shipping progress have been updated.');
    } catch (error) {
      if (requestStarted) {
        // A failed response can still follow a successful write. Never repeat the POST.
        try {
          const current = await readCart(); confirmedCart(current, {}, action.variantId);
          this.feedback(mutationConfirmed(before, current, action) ? 'Your change is confirmed. Your actual total and shipping progress are updated.' : 'Cart refreshed. Review your actual items and total before trying again.');
        } catch { this.showRecovery('We could not confirm the update. Refresh the cart before making another change.'); }
      } else this.feedback(error instanceof Error ? error.message : 'Your cart could not be updated.');
    } finally { if (!uncertainWrite) endMutation(); }
  }
}
if (!customElements.get('beest-cart-offer')) customElements.define('beest-cart-offer', BeestCartOffer);
